import { useMemo, useState, useEffect } from 'react';
import './App.css';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentSummaryIndex, setCurrentSummaryIndex] = useState(0);
  const [donateModalOpen, setDonateModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('bank'); // 'bank' or 'card'
  const pathname = useMemo(
    () => (typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '/'),
    []
  );
  const isAboutPage = pathname.startsWith('/about');
  const isPublicationsPage = pathname.startsWith('/publications');
  const publicationSlug = pathname.startsWith('/publications/')
    ? pathname.slice('/publications/'.length).split('/')[0].toLowerCase()
    : null;
  const isPublicationDetailPage = Boolean(publicationSlug);
  const isTeamPage = pathname.startsWith('/team');
  const isGalleryPage = pathname.startsWith('/gallery');
  const isContactPage = pathname.startsWith('/contact');
  const isStemPage = pathname.startsWith('/stem-education');
  const isDisabilitiesPage = pathname.startsWith('/disabilities-advocacy');
  const isWomenPage = pathname.startsWith('/women-empowerment');
  const isPartnerPage = pathname.startsWith('/partner');
  const isTermsPage = pathname.startsWith('/terms');
  const isCookiePolicyPage = pathname.startsWith('/cookie-policy');
  const closeMenu = () => setIsMenuOpen(false);
  const openDonateModal = () => {
    setDonateModalOpen(true);
    document.body.style.overflow = 'hidden';
  };
  const closeDonateModal = () => {
    setDonateModalOpen(false);
    document.body.style.overflow = '';
  };

  const galleryCategories = {
    'Disability Advocacy': [
      '/Disability Advocacy Folder/_MG_9128.JPG',
      '/Disability Advocacy Folder/_MG_9131.JPG',
      '/Disability Advocacy Folder/_MG_9134.JPG',
      '/Disability Advocacy Folder/_MG_9140.JPG',
      '/Disability Advocacy Folder/_MG_9155.JPG',
      '/Disability Advocacy Folder/_MG_9161.JPG',
      '/Disability Advocacy Folder/_MG_9164.JPG',
      '/Disability Advocacy Folder/_MG_9187.JPG',
      '/Disability Advocacy Folder/DRAN FLYER 1.jpeg',
      '/Disability Advocacy Folder/DRAN FLYER 2.jpeg',
      '/Disability Advocacy Folder/DRAN FLYER 3.jpeg',
      '/Disability Advocacy Folder/DRAN FLYER 4.jpeg',
      '/Disability Advocacy Folder/IMG-20250402-WA0002.jpg',
      '/Disability Advocacy Folder/IMG-20250402-WA0003.jpg',
      '/Disability Advocacy Folder/IMG-20250913-WA0008.jpg',
      '/Disability Advocacy Folder/IMG-20250923-WA0002.jpg',
      '/Disability Advocacy Folder/IMG-20250923-WA0004.jpg',
      '/Disability Advocacy Folder/IMG-20250923-WA0005.jpg',
      '/Disability Advocacy Folder/IMG-20250923-WA0006.jpg',
      '/Disability Advocacy Folder/IMG-20250923-WA0007.jpg',
      '/Disability Advocacy Folder/IMG-20251124-WA0008.jpg',
      '/Disability Advocacy Folder/IMG-20251210-WA0001.jpg',
      '/Disability Advocacy Folder/IMG-20251210-WA0002.jpg',
    ],
    'Girls in STEM': [
      '/Girls in STEM folder/Image for girls child education_.jpg',
      '/Girls in STEM folder/Image for girls in STEM link 2.jpg',
      '/Girls in STEM folder/Image for girls in STEM link.jpg',
      '/Girls in STEM folder/Image for girls STEM Education_.jpg',
      '/Girls in STEM folder/IMG-20250905-WA0004.jpg',
      '/Girls in STEM folder/IMG-20250905-WA0006.jpg',
      '/Girls in STEM folder/IMG-20250913-WA0012.jpg',
      '/Girls in STEM folder/IMG-20251110-WA0010.jpg',
      '/Girls in STEM folder/JMP-9524.jpg',
      '/Girls in STEM folder/STEM GIRLS Education support 1.jpg',
      '/Girls in STEM folder/STEM GIRLS Education support 2_.jpg',
      '/Girls in STEM folder/STEM GIRLS Education support 4.jpg',
      '/Girls in STEM folder/STEM GIRLS Education support 5.jpg',
    ],
    'Women Empowerment': [
      '/Women Empowerment folder/Image for women empowerment link.jpg',
      '/Women Empowerment folder/IMG-20250622-WA0003.jpg',
      '/Women Empowerment folder/IMG-20250622-WA0006.jpg',
      '/Women Empowerment folder/JMP-9340.jpg',
      '/Women Empowerment folder/JMP-9345.jpg',
      '/Women Empowerment folder/JMP-9362.jpg',
      '/Women Empowerment folder/The widows image 2.jpg',
      '/Women Empowerment folder/The widows.jpg',
      '/Women Empowerment folder/The widows(1).jpg',
      '/Women Empowerment folder/WhatsApp Image 2025-12-19 at 13.27.28.jpeg',
      '/Women Empowerment folder/Women empowerment support 3.jpg',
      '/Women Empowerment folder/Women empowerment support_.jpg',
    ],
  };

  // Flatten all images for lightbox navigation
  const allGalleryImages = Object.values(galleryCategories).flat();

  const yearSummaryItems = [
    {
      number: '15',
      text: 'Girls STEM Education Scholarships'
    },
    {
      number: '6',
      text: 'Women empowered with Vocational Business tools'
    },
    {
      number: '60',
      text: 'Widows supported with welfare packs'
    },
    {
      number: '3',
      text: 'Publications sponsored on Autism'
    },
    {
      number: '50',
      text: 'School uniforms for Hearing Impaired students'
    },
    {
      number: '5',
      text: 'Volunteer teachers in Hearing Impaired school given Appreciation grants'
    },
    {
      number: '1',
      text: 'Disability Rights Advocacy Network Roundtable convened'
    }
  ];

  // Auto-scroll through summary items
  useEffect(() => {
    if (!isAboutPage && !isPublicationsPage && !isTeamPage && !isGalleryPage && !isContactPage && !isStemPage && !isDisabilitiesPage && !isWomenPage) {
      const interval = setInterval(() => {
        setCurrentSummaryIndex((prev) => (prev + 1) % yearSummaryItems.length);
      }, 4000); // Change every 4 seconds

      return () => clearInterval(interval);
    }
  }, [isAboutPage, isPublicationsPage, isTeamPage, isGalleryPage, isContactPage, isStemPage, isDisabilitiesPage, isWomenPage, yearSummaryItems.length]);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allGalleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allGalleryImages.length) % allGalleryImages.length);
  };

  useEffect(() => {
    if (!lightboxOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxOpen(false);
        document.body.style.overflow = '';
      } else if (e.key === 'ArrowRight') {
        setCurrentImageIndex((prev) => (prev + 1) % allGalleryImages.length);
      } else if (e.key === 'ArrowLeft') {
        setCurrentImageIndex((prev) => (prev - 1 + allGalleryImages.length) % allGalleryImages.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, allGalleryImages.length]);

  const programHighlights = [
    {
      title: 'STEM Education for the Girl Child',
      description:
        'Delivering inclusive STEM learning labs, mentorship, and scholarships that equip girls with future-ready skills.',
      image: '/Image for girls in STEM link.jpg',
      slug: '/stem-education'
    },
    {
      title: 'Disabilities Advocacy',
      description:
        'Championing disability rights through policy advocacy, assistive technology access, and community inclusion programs.',
      image: '/Image for disabilities advocacy link.jpg',
      slug: '/disabilities-advocacy'
    },
    {
      title: 'Women Empowerment',
      description:
        'Leadership development workshops, micro-enterprise grants, and personal growth programs so women can thrive economically.',
      image: '/Image for women empowerment link 2.jpg',
      slug: '/women-empowerment'
    }
  ];

  return (
    <div className="app">
      <header className="site-header">
        <div className="site-header__top">
          <div className="site-header__contact">
            <span>Victoria Island, Lagos</span>
            <span>+234 915 641 7249</span>
            <span>info@drvufoundation.com</span>
          </div>
          <div className="site-header__social">
            <a href="#facebook" aria-label="Facebook">
              <span className="visually-hidden">Facebook</span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22 12a10 10 0 1 0-11.562 9.875v-6.988H7.898V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.462h-1.26c-1.242 0-1.63.771-1.63 1.562V12h2.773l-.443 2.887h-2.33v6.988A10.002 10.002 0 0 0 22 12Z" />
              </svg>
            </a>
            <a href="https://www.instagram.com/drvuefoundation?igsh=MW80Y3dxeGI2aHF4dQ==" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <span className="visually-hidden">Instagram</span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5A4.25 4.25 0 0 0 7.75 20.5h8.5a4.25 4.25 0 0 0 4.25-4.25v-8.5A4.25 4.25 0 0 0 16.25 3.5h-8.5Zm9.75 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-5.5 1.75a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Zm0 1.5a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Z" />
              </svg>
            </a>
            <a href="https://www.youtube.com/@DrVUEFoundation" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
              <span className="visually-hidden">YouTube</span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21.582 7.2a2.248 2.248 0 0 0-1.583-1.59C18.25 5.2 12 5.2 12 5.2s-6.25 0-7.999.41A2.248 2.248 0 0 0 2.418 7.2C2 8.958 2 12 2 12s0 3.042.418 4.8a2.248 2.248 0 0 0 1.583 1.59c1.749.41 7.999.41 7.999.41s6.25 0 7.999-.41a2.248 2.248 0 0 0 1.583-1.59C22 15.042 22 12 22 12s0-3.042-.418-4.8ZM10 15.27V8.73L15.5 12 10 15.27Z" />
              </svg>
            </a>
          </div>
        </div>

        <nav className="site-nav">
          <div className="site-nav__brand">
            <img src="/logo1.png" alt="Dr. VUE Foundation" className="site-nav__brand-logo" />
          </div>

          <ul className={`site-nav__menu ${isMenuOpen ? 'is-open' : ''}`}>
            <li>
              <a href="/" onClick={closeMenu}>Home</a>
            </li>
            <li>
              <a href="/about" onClick={closeMenu}>About</a>
            </li>
            <li>
              <a href="/publications" onClick={closeMenu}>Publications</a>
            </li>
            <li>
              <a href="/team" onClick={closeMenu}>Team</a>
            </li>
            <li>
              <a href="/gallery" onClick={closeMenu}>Gallery</a>
            </li>
            <li>
              <a href="/contact" onClick={closeMenu}>Contact</a>
            </li>
            <li>
              <a href="#donate" onClick={(e) => { e.preventDefault(); openDonateModal(); closeMenu(); }}>Donate</a>
            </li>
          </ul>

          <div className="site-nav__controls">
            <button className="site-nav__cta" onClick={openDonateModal}>Donate</button>
            <button
              className={`site-nav__hamburger ${isMenuOpen ? 'is-active' : ''}`}
              aria-label="Toggle navigation"
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen(value => !value)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </nav>
      </header>

      <main>
        {!isAboutPage && !isPublicationsPage && !isTeamPage && !isGalleryPage && !isContactPage && !isStemPage && !isDisabilitiesPage && !isWomenPage && !isPartnerPage && !isTermsPage && !isCookiePolicyPage && (
          <section className="hero" id="home">
            <div className="hero__grid">
              <div className="hero__content">
                <span className="hero__overline">Driving Inclusion, Empowerment & Excellence</span>
                <h1 className="hero__title">Dr. VUE Foundation</h1>
                <p className="hero__description">
                  Unlocking the full potential of people with disabilities, girls, and women through inclusive education,
                  empowerment programs, and sustainable opportunities.
                </p>
                <div className="hero__actions">
                  <a href="/gallery" className="button button--primary">View Our Projects</a>
                </div>
              </div>
            </div>
          </section>
        )}

        {isAboutPage && (
          <section className="about-hero" id="about">
            <div className="about-hero__content">
              <span className="about-hero__eyebrow">Our Story</span>
              <h1 className="about-hero__title">About Us</h1>
              <p className="about-hero__subtitle">
                We promote inclusion by empowering people with disabilities, girls, and women with the tools, voice, and
                opportunities they need to thrive.
              </p>
            </div>
          </section>
        )}

        {isAboutPage && (
          <section className="about-story">
            <div className="about-story__inner">
              <div className="about-story__bio-container">
                <div className="about-story__bio-image">
                  <img src="/drvue.jpeg" alt="Dr. (Mrs.) Victoria Ulo Enyenihi" />
                </div>
              <div className="about-story__bio">
                <h2 className="about-story__heading">Biography of Late Dr. (Mrs.) Victoria Ulo Enyenihi</h2>
                <p>
                  Late Dr. (Mrs.) Victoria Ulo Enyenihi (née Mkpokpo) was born on July 6, 1944, as the first child of Mr.
                  Sunday Akpan Mkpokpo, a distinguished headmaster of Ikot Idem Village, Odoro Ikot Clan in Essien Udim LGA,
                  Akwa Ibom State. She was the beloved only child of her mother, Mrs. Mabel Mkpokpo (née Udo Akpabio). Raised by
                  visionary and supportive parents, Victoria consistently excelled academically and emerged as a natural leader among her peers.
                </p>
                <p>
                  She pursued her secondary education at Cornelie-Cornelia College, Uyo (CCC), where she served as Head Girl and
                  graduated with credit passes in Division One of the West African School Certificate (WASC) Examination. Her
                  leadership qualities continued to flourish at Queen's College, Enugu, where she was appointed a prefect.
                  Victoria&rsquo;s academic excellence earned her three A-Level passes in science subjects, securing her admission
                  to the University of Ibadan in 1963 to study Chemistry. Supported by scholarships, she graduated with a
                  Second-Class Honours degree in Chemistry.
                </p>
                <p>
                  In 1968, she married Dr. Ulo Kpounko Enyenihi, and together they were blessed with five children. In June 1969, Victoria accompanied her husband to the United States of America during his research studies, where she developed a renewed passion for furthering her education. Upon their return to Ibadan, she enrolled for a Ph.D. program in Natural Products Chemistry under the supervision of Professor Donald Ekong.
                </p>
                <p>
                  In 1974, she successfully defended her thesis titled "Synthesis and Ultra-violet Spectra of 4-substituted Coumarins; Chemistry of Roots of Fagara Zanthoxyloides LAM." With this achievement, she made history as the first woman from the old Cross River State (which today includes Akwa Ibom State) to earn a Ph.D. in Chemistry.
                </p>
                <p>
                  That same year, she moved with her family to Cross River State, and in 1975, she was appointed the first female Chemistry Lecturer at the University of Calabar. Remarkably, the findings from her doctoral research led to the discovery of an aqueous extract with anti-sickling properties. In collaboration with pharmaceutical chemists at Obafemi Awolowo University, this research contributed to the development of a drug for the prevention and management of sickle cell crises, marking a significant contribution to science and humanity at the young age of 30.
                </p>
                <p>
                  <em>Note: Late Dr. (Mrs.) Victoria Ulo Enyenihi's research contribution is historical, not a current foundation program.</em>
                </p>
                <p>
                  Tragically, Dr. Victoria Ulo Enyenihi&rsquo;s promising life was cut short in June 1975, at the age of 31. Her legacy, however, endures. In September 2022, she was posthumously honored with the "Rescue Ambassador" award by the Coalition of Civil Society Organizations on Gender Equality, Child Protection, Leadership, and Good Governance (CSOGCLG) Nigeria, in recognition of her unparalleled impact, selfless service to humanity, and contributions to the development of knowledge.
                </p>
                <p>
                  A trailblazer, a role model, and a beacon of inspiration, we continue to celebrate the remarkable life of Late Dr. (Mrs.) Victoria Ulo Enyenihi. May her soul, and the souls of all the faithful departed, through the mercy of God, rest in perfect peace. Amen.
                </p>
                </div>
              </div>
            </div>

            <div className="about-story__mission-visual">
              <div className="about-story__values">
                <article className="about-story__value-card">
                  <div className="about-story__value-overline">What Drives Us</div>
                  <p>
                    We are driven by a commitment to inclusion, education, and empowerment unlocking the potential of people with disabilities, girls, and women to build a more equitable future.
                  </p>
                </article>
                <article className="about-story__value-card">
                  <h3>Our Mission</h3>
                  <p>
                    To advocate for laws and policies that promote awareness, uphold the rights and inclusion of people with disabilities, and champion Science, Technology, Engineering & Maths (STEM) Education for the Girl Child, while advancing the economic empowerment of women to drive gender equality and sustainable development.
                  </p>
                </article>
                <article className="about-story__value-card">
                  <h3>Our Vision</h3>
                  <p>
                    To be a leading center of excellence advancing the rights and full inclusion of people with disabilities, while empowering girls and women through education, leadership, and economic self-sufficiency to build a more equitable and prosperous society.
                  </p>
                </article>
              </div>
            </div>

            <div className="about-story__images-section">
              <div className="about-story__images-horizontal">
                {[
                  'About Us Folder/Akwa ibom Commissioner for Humanitarian Affairs presenting the gift_.jpg',
                  'About Us Folder/Copy of JMP-9215.jpg',
                  'About Us Folder/Image of items for widows support 2.jpg',
                  'About Us Folder/Image of items for widows support_.jpg',
                  'About Us Folder/JMP-8685.jpg',
                  'About Us Folder/JMP-8701.jpg',
                  'About Us Folder/JMP-8708.jpg',
                  'About Us Folder/JMP-8710.jpg',
                  'About Us Folder/JMP-8715.jpg',
                  'About Us Folder/JMP-8739.jpg',
                  'About Us Folder/JMP-9032.jpg',
                  'About Us Folder/JMP-9047.jpg',
                  'About Us Folder/JMP-9052.jpg',
                  'About Us Folder/JMP-9058.jpg',
                  'About Us Folder/JMP-9063.jpg',
                  'About Us Folder/JMP-9074.jpg',
                  'About Us Folder/JMP-9087.jpg',
                  'About Us Folder/JMP-9091.jpg',
                  'About Us Folder/JMP-9099.jpg',
                  'About Us Folder/JMP-9123.jpg',
                  'About Us Folder/JMP-9125.jpg',
                  'About Us Folder/JMP-9217.jpg',
                  'About Us Folder/JMP-9312.jpg',
                  'About Us Folder/JMP-9477.jpg',
                  'About Us Folder/JMP-9512.jpg',
                  'About Us Folder/Widows support image.jpg',
                  'About Us Folder/Women empowerment support 4.jpg',
                ].map((imagePath, index) => (
                  <img 
                    key={index}
                    src={encodeURI(`/${imagePath}`)} 
                    alt={`Foundation activities ${index + 1}`} 
                    className="about-story__image-horizontal" 
                  />
                ))}
                </div>
              </div>

            <div className="about-story__core" style={{ marginBottom: '2rem' }}>
              <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem', background: 'rgba(255, 255, 255, 0.94)', borderRadius: '24px', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)' }}>
                <h3 className="about-story__core-title" style={{ marginTop: 0, color: 'rgba(21, 13, 32, 0.95)' }}>Foundation Activities and Impact</h3>
                <p style={{ color: 'rgba(21, 13, 32, 0.85)', lineHeight: '1.8', fontSize: '1.05rem' }}>
                  Since inception, the Foundation has achieved measurable community impact, including the provision of <strong>15 Girls' STEM Education Scholarships</strong>, <strong>50 uniforms</strong> for hearing-impaired students, <strong>three autism publications</strong>, and <strong>one national disability advocacy roundtable</strong>. We have also empowered <strong>six women</strong> with vocational tools and supported <strong>sixty widows</strong> with welfare packs, demonstrating our commitment to equity, dignity, and inclusive opportunity.
                </p>
              </div>
            </div>

            <div className="about-story__core">
              <div className="about-story__core-values">
                <h3 className="about-story__core-title">Core Values</h3>
                <ul className="about-story__core-list">
                  <li>
                    <strong>Humanism:</strong> Championing dignity, inclusion, and equity for all people.
                  </li>
                  <li>
                    <strong>Leadership:</strong> Inspiring and empowering individuals to drive positive change and innovation.
                  </li>
                  <li>
                    <strong>Excellence:</strong> Committing to the highest standards in advocacy, education, and empowerment.
                  </li>
                </ul>
              </div>

              <div className="about-story__aims">
                <h3 className="about-story__core-title">Aims</h3>
                <p>
                  The foundation is committed to advocating for the rights, inclusion, and empowerment of people with disabilities
                  and to promoting the education and leadership of girls and women, especially through Science, Technology, Engineering & Maths (STEM) Education for the Girl Child. It strives to improve
                  the quality of life for individuals with disabilities, break gender barriers, and foster a just, inclusive, and
                  prosperous society.
                </p>
              </div>
            </div>

            <div className="about-story__objectives">
              <h3 className="about-story__objectives-title">Objectives</h3>
              <ul className="about-story__objectives-list">
                <li>
                  <strong>Advocacy and Awareness:</strong> Promote laws, policies, and campaigns that protect the rights of people
                  with disabilities and advance gender equality in education and leadership.
                </li>
                <li>
                  <strong>Education and Empowerment:</strong> Facilitate access to quality Science, Technology, Engineering & Maths (STEM) Education for the Girl Child, while providing tailored support and mentorship opportunities.
                </li>
                <li>
                  <strong>Accessibility and Skill Development:</strong> Foster inclusive communities, support families and caregivers, and deliver life skills development and inclusive vocational training for people with disabilities.
                </li>
                <li>
                  <strong>Community and Capacity Building:</strong> Foster inclusive communities, support families and caregivers, and
                  strengthen networks that empower girls and women to thrive in STEM careers and entrepreneurship through Science, Technology, Engineering & Maths (STEM) Education for the Girl Child.
                </li>
                <li>
                  <strong>Economic Empowerment:</strong> Partner with organizations to create employment opportunities, provide
                  entrepreneurial training, and promote innovation among women and people with disabilities.
                </li>
                <li>
                  <strong>Technology and Innovation:</strong> Leverage assistive technologies, offer coding, robotics, and leadership
                  programs, and promote continuous professional development.
                </li>
                <li>
                  <strong>Impact Measurement and Growth:</strong> Establish measurable outcomes, scale successful initiatives, and
                  continually refine programs to better serve individuals and communities.
                </li>
              </ul>
            </div>
          </section>
        )}

        {isPublicationsPage && (
          <section className={`publications ${isPublicationDetailPage ? 'publications--detail' : ''}`} id="publications">
            {isPublicationDetailPage && publicationSlug === 'autism-factsheet' ? (
              <div className="publication-detail">
                <a href="/publications" className="publication-detail__back">← Back to Publications</a>
                <article className="publication-card publication-card--detail">
                  <div className="publication-card__meta">
                    <span className="publication-card__tag">Disability Rights</span>
                    <time dateTime="2025-10-26">October 26, 2025</time>
                    <span aria-hidden="true">·</span>
                    <span>No Comments</span>
                  </div>
                  <h1 className="publication-card__heading publication-card__heading--detail">Autism Factsheet</h1>
                  <p className="publication-card__excerpt">
                    A clear and accessible overview of Autism Spectrum Disorder (ASD)—what it is, common characteristics, strengths,
                    and practical ways families, schools, and communities can offer support.
                  </p>
                  <div className="publication-card__downloads">
                    <h4 className="publication-card__downloads-title">Autism Fact Sheets</h4>
                    <ul className="publication-card__downloads-list">
                      <li>
                        <a href={encodeURI('/Disability Advocacy Folder/DRAN FLYER 1.jpeg')} target="_blank" rel="noopener noreferrer">
                          Fact Sheet on Autism – English Version
                        </a>
                      </li>
                      <li>
                        <a href={encodeURI('/Disability Advocacy Folder/DRAN FLYER 2.jpeg')} target="_blank" rel="noopener noreferrer">
                          Fact Sheet on Autism – Pidgin Version
                        </a>
                      </li>
                      <li>
                        <a href={encodeURI('/Disability Advocacy Folder/DRAN FLYER 3.jpeg')} target="_blank" rel="noopener noreferrer">
                          Fact Sheet on Autism – Annang Version
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="publication-card__body">
                    <h4 className="publication-card__subheading">What is Autism?</h4>
                    <p>
                      Autism, or Autism Spectrum Disorder (ASD), is a neurological and developmental condition that affects how a
                      person communicates, learns, behaves, and interacts with others. It is described as a spectrum because people
                      experience a wide range of strengths and challenges.
                    </p>
                    <h5 className="publication-card__subheading">Key Characteristics</h5>
                    <p>People with autism may experience differences in how they communicate, learn, and relate to the world:</p>
                    <ul className="publication-card__list">
                      <li><strong>Communication &amp; social interaction:</strong> difficulty with back-and-forth conversation, understanding body language, facial expressions, or tone of voice, and a preference for routines or spending time alone.</li>
                      <li><strong>Behavioral patterns:</strong> repetitive movements or speech (such as hand-flapping or repeating phrases), strong interests in specific topics, sensory sensitivities to sound, light, textures, or smells, and a strong need for sameness and predictability.</li>
                    </ul>
                    <h5 className="publication-card__subheading">Strengths Often Seen in Autism</h5>
                    <p>Many autistic people bring unique strengths to their families, schools, workplaces, and communities, including:</p>
                    <ul className="publication-card__list">
                      <li>Strong memory and attention to detail.</li>
                      <li>Deep focus and persistence in areas of interest.</li>
                      <li>Logical and analytical thinking and creative problem‑solving.</li>
                      <li>Honesty, reliability, and direct communication.</li>
                    </ul>
                    <h5 className="publication-card__subheading">Causes</h5>
                    <p>There is no single cause of autism. Research shows that a combination of genetic and neurological factors contributes to its development. Parenting styles or vaccines do <strong>not</strong> cause autism.</p>
                    <h5 className="publication-card__subheading">Diagnosis</h5>
                    <p>Autism is typically diagnosed by healthcare professionals such as psychologists, psychiatrists, or developmental pediatricians. Assessment usually involves developmental history, observation, and standardized tools that look at communication, social interaction, and behavior. Signs can appear in early childhood but may also be recognised later in adolescence or adulthood.</p>
                    <h5 className="publication-card__subheading">Prevalence</h5>
                    <p>Autism occurs in people of all genders, cultures, and backgrounds. Global estimates suggest that around <strong>1–2%</strong> of the population may be autistic. It is more frequently diagnosed in boys, although autism in girls is often under‑recognised.</p>
                    <h5 className="publication-card__subheading">Support &amp; Treatment</h5>
                    <p>Autism is a lifelong condition, but with the right support, autistic people can thrive. Helpful interventions may include:</p>
                    <ul className="publication-card__list">
                      <li>Speech and language therapy.</li>
                      <li>Occupational therapy and sensory integration support.</li>
                      <li>Individualised educational support and classroom accommodations.</li>
                      <li>Social skills development and mentoring programmes.</li>
                      <li>Family, peer, and community support networks.</li>
                    </ul>
                    <p>The goal of support is to build skills, confidence, communication, and independence—not to "cure" who a person is.</p>
                    <h5 className="publication-card__subheading">Autism &amp; Identity</h5>
                    <p>Many people understand autism not only as a diagnosis but also as part of identity and culture. The concept of <strong>neurodiversity</strong> recognises that different ways of thinking, learning, and experiencing the world are natural and valuable.</p>
                    <h5 className="publication-card__subheading">Inclusive Language</h5>
                    <p>Preferred language can vary. Some people identify as an "autistic person," while others prefer "person with autism." The most respectful approach is to ask individuals how they would like to be described and to honour their choice.</p>
                  </div>
                </article>
              </div>
            ) : (
              <>
            <header className="publications__hero">
              <span className="publications__breadcrumb">Home / Publications</span>
              <h1 className="publications__title">Publications</h1>
            </header>

            <div className="publications__intro">
              <h2>Stay Updated with Publications From Us</h2>
              <p>
                Explore policy insights, impact stories, and research related to our Objectives, or produced by the Dr. VUE Foundation or our Trustees and strategic partners. Each publication underscores our commitment to inclusion, leadership, and sustainable development for girls, women, and people with disabilities.
              </p>
            </div>

            <div className="publications__list">
              <article className="publication-card">
                <div className="publication-card__meta">
                  <span className="publication-card__tag">Disability Rights</span>
                  <time dateTime="2025-10-26">October 26, 2025</time>
                  <span aria-hidden="true">·</span>
                  <span>No Comments</span>
                </div>
                <h3 className="publication-card__heading">Autism Factsheet</h3>
                <p className="publication-card__excerpt">
                  A clear and accessible overview of Autism Spectrum Disorder (ASD)—what it is, common characteristics, strengths,
                  and practical ways families, schools, and communities can offer support.
                </p>
                <a className="publication-card__link" href="/publications/autism-factsheet">Read More</a>
              </article>
            </div>
              </>
            )}
          </section>
        )}

        {isTeamPage && (
          <section className="team" id="team">
            <header className="team__hero">
              <span className="team__breadcrumb">Home / Board of Trustees
              </span>
              <h1 className="team__title">Board Of Trustees</h1>
            </header>

            <div className="team__intro">
              <p>“Behind every achievement is a team that believed and worked to make it real.”</p>
            </div>

            <div className="team__grid">
              <article className="team-card">
                <figure className="team-card__media">
                  <img
                    src="/Team-1.jpeg"
                    alt="Mrs Enobong Teresa Enyenihi Ozor"
                  />
                </figure>
                <div className="team-card__body">
                  <h3>Mrs Enobong Teresa Enyenihi Ozor</h3>
                  <span className="team-card__role">Founder & Chairperson Board of Trustees</span>
                  <ul className="team-card__social">
                    <li>
                      <a href="#facebook" aria-label="Mrs Enobong Teresa Enyenihi Ozor on Facebook">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M22 12a10 10 0 1 0-11.562 9.875v-6.988H7.898V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.462h-1.26c-1.242 0-1.63.771-1.63 1.562V12h2.773l-.443 2.887h-2.33v6.988A10.002 10.002 0 0 0 22 12Z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#instagram" aria-label="Mrs Enobong Teresa Enyenihi Ozor on Instagram">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5A4.25 4.25 0 0 0 7.75 20.5h8.5a4.25 4.25 0 0 0 4.25-4.25v-8.5A4.25 4.25 0 0 0 16.25 3.5h-8.5Zm9.75 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-5.5 1.75a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Zm0 1.5a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#twitter" aria-label="Mrs Enobong Teresa Enyenihi Ozor on Twitter">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#linkedin" aria-label="Mrs Enobong Teresa Enyenihi Ozor on LinkedIn">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M20.447 20.452h-3.554v-5.564c0-1.328-.027-3.044-1.852-3.044-1.853 0-2.136 1.445-2.136 2.939v5.669H9.504V9h3.416v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.064-2.065c0-1.136.928-2.063 2.064-2.063 1.136 0 2.064.927 2.064 2.063 0 1.137-.928 2.065-2.064 2.065zm1.785 13.019H3.552V9h3.57z" />
                        </svg>
                      </a>
                    </li>
                  </ul>
                </div>
              </article>

              <article className="team-card">
                <figure className="team-card__media">
                  <img
                    src="/dotun.jpeg"
                    alt="Mrs Dotun Akande"
                  />
                </figure>
                <div className="team-card__body">
                  <h3>Mrs. Dotun Akande</h3>
                  <span className="team-card__role">Managing Director, Patrick Speech and Languages Centre | Trustee</span>
                  <ul className="team-card__social">
                    <li>
                      <a href="#facebook" aria-label="Mrs Dotun Akande on Facebook">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M22 12a10 10 0 1 0-11.562 9.875v-6.988H7.898V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.462h-1.26c-1.242 0-1.63.771-1.63 1.562V12h2.773l-.443 2.887h-2.33v6.988A10.002 10.002 0 0 0 22 12Z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#instagram" aria-label="Mrs Dotun Akande on Instagram">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5A4.25 4.25 0 0 0 7.75 20.5h8.5a4.25 4.25 0 0 0 4.25-4.25v-8.5A4.25 4.25 0 0 0 16.25 3.5h-8.5Zm9.75 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-5.5 1.75a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Zm0 1.5a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#twitter" aria-label="Mrs Dotun Akande on Twitter">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#linkedin" aria-label="Mrs Dotun Akande on LinkedIn">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M20.447 20.452h-3.554v-5.564c0-1.328-.027-3.044-1.852-3.044-1.853 0-2.136 1.445-2.136 2.939v5.669H9.504V9h3.416v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.064-2.065c0-1.136.928-2.063 2.064-2.063 1.136 0 2.064.927 2.064 2.063 0 1.137-.928 2.065-2.064 2.065zm1.785 13.019H3.552V9h3.57z" />
                        </svg>
                      </a>
                    </li>
                  </ul>
                </div>
              </article>

              <article className="team-card">
                <figure className="team-card__media">
                  <img
                    src="/ogbona.jpeg"
                    alt="Ms Maureen Ogbonna"
                  />
                </figure>
                <div className="team-card__body">
                  <h3>Ms. Maureen Ogbonna</h3>
                  <span className="team-card__role">Founder & CEO, Valllorie NIG LTD | Trustee</span>
                  <ul className="team-card__social">
                    <li>
                      <a href="#facebook" aria-label="Ms Maureen Ogbonna on Facebook">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M22 12a10 10 0 1 0-11.562 9.875v-6.988H7.898V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.462h-1.26c-1.242 0-1.63.771-1.63 1.562V12h2.773l-.443 2.887h-2.33v6.988A10.002 10.002 0 0 0 22 12Z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#instagram" aria-label="Ms Maureen Ogbonna on Instagram">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5A4.25 4.25 0 0 0 7.75 20.5h8.5a4.25 4.25 0 0 0 4.25-4.25v-8.5A4.25 4.25 0 0 0 16.25 3.5h-8.5Zm9.75 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-5.5 1.75a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Zm0 1.5a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#twitter" aria-label="Ms Maureen Ogbonna on Twitter">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#linkedin" aria-label="Ms Maureen Ogbonna on LinkedIn">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M20.447 20.452h-3.554v-5.564c0-1.328-.027-3.044-1.852-3.044-1.853 0-2.136 1.445-2.136 2.939v5.669H9.504V9h3.416v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.064-2.065c0-1.136.928-2.063 2.064-2.063 1.136 0 2.064.927 2.064 2.063 0 1.137-.928 2.065-2.064 2.065zm1.785 13.019H3.552V9h3.57z" />
                        </svg>
                      </a>
                    </li>
                  </ul>
                </div>
              </article>

              <article className="team-card">
                <figure className="team-card__media">
                  <img
                    src="/arit.jpg"
                    alt="Mrs Arit Iwatt"
                  />
                </figure>
                <div className="team-card__body">
                  <h3>Mrs Arit Iwatt</h3>
                  <span className="team-card__role">Secretary Board of Trustees</span>
                  <ul className="team-card__social">
                    <li>
                      <a href="#facebook" aria-label="Mrs Arit Iwatt on Facebook">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M22 12a10 10 0 1 0-11.562 9.875v-6.988H7.898V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.462h-1.26c-1.242 0-1.63.771-1.63 1.562V12h2.773l-.443 2.887h-2.33v6.988A10.002 10.002 0 0 0 22 12Z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#instagram" aria-label="Mrs Arit Iwatt on Instagram">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5A4.25 4.25 0 0 0 7.75 20.5h8.5a4.25 4.25 0 0 0 4.25-4.25v-8.5A4.25 4.25 0 0 0 16.25 3.5h-8.5Zm9.75 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-5.5 1.75a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Zm0 1.5a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#twitter" aria-label="Mrs Arit Iwatt on Twitter">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#linkedin" aria-label="Mrs Arit Iwatt on LinkedIn">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M20.447 20.452h-3.554v-5.564c0-1.328-.027-3.044-1.852-3.044-1.853 0-2.136 1.445-2.136 2.939v5.669H9.504V9h3.416v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.064-2.065c0-1.136.928-2.063 2.064-2.063 1.136 0 2.064.927 2.064 2.063 0 1.137-.928 2.065-2.064 2.065zm1.785 13.019H3.552V9h3.57z" />
                        </svg>
                      </a>
                    </li>
                  </ul>
                </div>
              </article>

              <article className="team-card">
                <figure className="team-card__media">
                  <div style={{ width: '100%', height: '280px', background: '#f8f5fc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(21, 13, 32, 0.4)' }}>
                    <span>No image available</span>
                  </div>
                </figure>
                <div className="team-card__body">
                  <h3>Pst Samuel Mkpokpo</h3>
                  <span className="team-card__role">Trustee</span>
                  <ul className="team-card__social">
                    <li>
                      <a href="#facebook" aria-label="Pst Samuel Mkpokpo on Facebook">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M22 12a10 10 0 1 0-11.562 9.875v-6.988H7.898V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.462h-1.26c-1.242 0-1.63.771-1.63 1.562V12h2.773l-.443 2.887h-2.33v6.988A10.002 10.002 0 0 0 22 12Z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#instagram" aria-label="Pst Samuel Mkpokpo on Instagram">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5A4.25 4.25 0 0 0 7.75 20.5h8.5a4.25 4.25 0 0 0 4.25-4.25v-8.5A4.25 4.25 0 0 0 16.25 3.5h-8.5Zm9.75 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-5.5 1.75a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Zm0 1.5a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#twitter" aria-label="Pst Samuel Mkpokpo on Twitter">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href="#linkedin" aria-label="Pst Samuel Mkpokpo on LinkedIn">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M20.447 20.452h-3.554v-5.564c0-1.328-.027-3.044-1.852-3.044-1.853 0-2.136 1.445-2.136 2.939v5.669H9.504V9h3.416v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.064-2.065c0-1.136.928-2.063 2.064-2.063 1.136 0 2.064.927 2.064 2.063 0 1.137-.928 2.065-2.064 2.065zm1.785 13.019H3.552V9h3.57z" />
                        </svg>
                      </a>
                    </li>
                  </ul>
                </div>
              </article>
            </div>
          </section>
        )}

        {isTeamPage && (
          <section className="partner" id="partner">
            <div className="partner__inner">
              <span className="partner__tag">Partner With Us</span>
              <h2 className="partner__title">
                Advocating Inclusion. Empowering People with Disabilities & Females. Championing Excellence.
              </h2>
              <p className="partner__description">
                Join our community of allies investing in equitable access to education, empowerment resources, and mentorship.
                Together, we can unlock opportunities and create sustainable change for women and individuals with disabilities.
              </p>
              <div className="partner__actions">
                <a className="button button--secondary" href="/partner">Partner With Us</a>
              </div>
            </div>
          </section>
        )}

        {isGalleryPage && (
          <>
            <section className="gallery-hero" id="gallery">
              <div className="gallery-hero__content">
                <span className="gallery-hero__eyebrow">Moments Of Impact</span>
                <h1 className="gallery-hero__title">Gallery</h1>
                <p className="gallery-hero__description">
                  Discover the faces, places, and stories behind our mission. Each photo celebrates community, compassion, and
                  inclusion in action.
                </p>
              </div>
            </section>

            <section className="gallery-grid">
              {Object.entries(galleryCategories).map(([category, images]) => {
                const categoryStartIndex = Object.values(galleryCategories)
                  .slice(0, Object.keys(galleryCategories).indexOf(category))
                  .reduce((sum, arr) => sum + arr.length, 0);
                
                return (
                  <div key={category} className="gallery-category">
                    <h2 className="gallery-category__title">{category}</h2>
              <div className="gallery-grid__inner">
                      {images.map((image, index) => {
                        const globalIndex = categoryStartIndex + index;
                        return (
                  <div 
                    key={index} 
                    className="gallery-grid__item"
                            onClick={() => openLightbox(globalIndex)}
                  >
                            <img src={encodeURI(image)} alt={`${category} ${index + 1}`} />
                  </div>
                        );
                      })}
              </div>
                  </div>
                );
              })}
            </section>

            {lightboxOpen && (
              <div className="lightbox" onClick={closeLightbox}>
                <button 
                  className="lightbox__close" 
                  onClick={closeLightbox}
                  aria-label="Close lightbox"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
                <button 
                  className="lightbox__prev" 
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  aria-label="Previous image"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
                <button 
                  className="lightbox__next" 
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  aria-label="Next image"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
                <div className="lightbox__content" onClick={(e) => e.stopPropagation()}>
                  <img 
                    src={encodeURI(allGalleryImages[currentImageIndex])} 
                    alt={`Gallery ${currentImageIndex + 1}`}
                    className="lightbox__image"
                  />
                  <div className="lightbox__counter">
                    {currentImageIndex + 1} / {allGalleryImages.length}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {isContactPage && (
          <>
            <section className="contact-hero" id="contact">
              <div className="contact-hero__content">
                <span className="contact-hero__eyebrow">Let’s Build Your Vision</span>
                <h1 className="contact-hero__title">Connect With Our Team</h1>
                <p className="contact-hero__description">
                  Have questions about our initiatives, partnerships, or volunteering opportunities? Reach out—we’re ready to
                  collaborate and create meaningful impact together.
                </p>
              </div>
            </section>

            <section className="contact">
              <div className="contact__inner">
                <div className="contact__info">
                  <header>
                    <h2>Let’s Build Your Vision</h2>
                    <p className="contact__tagline">“Have a Vision? We Build It Together.”</p>
                  </header>
                  <p>
                    Whether you have questions about our programs, partnership opportunities, or ongoing initiatives, our team is here to support you. Share your ideas, collaboration requests or impact stories, we're just a message away.
                  </p>

                  <ul className="contact__list">
                    <li>
                      <span aria-hidden="true" className="contact__icon">📞</span>
                      <a href="tel:+2349156417249">+234 915 641 7249</a>
                    </li>
                    <li>
                      <span aria-hidden="true" className="contact__icon">📍</span>
                      <a href="https://maps.app.goo.gl/placeholder">Victoria Island, Lagos, Nigeria</a>
                    </li>
                    <li>
                      <span aria-hidden="true" className="contact__icon">✉️</span>
                      <a href="mailto:info@drvufoundation.com">info@drvufoundation.com</a>
                    </li>
                  </ul>

                  <div className="contact__forms">
                    <h3>Contact Foundation</h3>
                    <ul className="contact__forms-list">
                      <li>
                        <a href="https://docs.google.com/forms/d/1e0otz-adhON_bkRgjamAqiu3CkP2B1CAyrbEdwHcaaM/viewform" target="_blank" rel="noopener noreferrer" className="contact__form-link">
                          Contact & Inquiry Form
                        </a>
                    </li>
                  </ul>
                  </div>
                </div>

                <div className="contact__form-section">
                  <h3 className="contact__form-section-title">General Inquiry</h3>
                <form className="contact__form">
                  <label className="contact__label" htmlFor="contact-name">Name</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    placeholder="Full name"
                    autoComplete="name"
                    required
                  />

                  <label className="contact__label" htmlFor="contact-email">Email</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                  />

                  <label className="contact__label" htmlFor="contact-message">Message</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows="5"
                    placeholder="Share how we can partner with you"
                    required
                  />

                  <button type="submit" className="button button--primary contact__submit">Send Message</button>
                </form>
                </div>
              </div>
            </section>
          </>
        )}

        {isStemPage && (
          <>
            <section className="initiative-hero" id="stem-education">
              <div className="initiative-hero__content">
                <span className="initiative-hero__eyebrow">Our Focus Areas</span>
                <h1 className="initiative-hero__title">Science, Technology, Engineering & Maths (STEM) Education for the Girl Child</h1>
                <p className="initiative-hero__description">
                  Delivering inclusive STEM learning labs, mentorship, and scholarships that equip girls with future-ready skills.
                </p>
              </div>
            </section>

            <section className="initiative-content">
              <div className="initiative-content__inner">
                <div className="initiative-content__section">
                  <h2>Empowering Girls Through Science, Technology, Engineering & Maths (STEM) Education</h2>
                  <p>
                    Our Science, Technology, Engineering & Maths (STEM) Education for the Girl Child initiative is dedicated to breaking down barriers and creating opportunities for girls to excel in Science, Technology, Engineering, and Mathematics. We believe that every girl deserves access to quality STEM education, regardless of their background or circumstances.
                  </p>
                  <p>
                    Through our comprehensive programs, we provide hands-on learning experiences, mentorship from industry professionals, and financial support to ensure that girls can pursue their passions in STEM fields and build successful careers.
                  </p>
                </div>

                <div className="initiative-content__section">
                  <h3>Scholarship Program</h3>
                  <p>
                    Our scholarship program commenced in the 2024/2025 Academic year, for the 2025-2026 academic session providing comprehensive financial support to girls pursuing Science, Technology, Engineering & Maths (STEM) Education. The program is designed to remove financial barriers and enable girls to focus on their academic and professional development. Currently we have <strong>5 Secondary School</strong> and <strong>4 University Students</strong> on the scheme.
                  </p>
                  <p>
                    The Foundation also has annual sponsorship of National Examination Council ("NECO") exams for <strong>5 Sciences students</strong> of Cornellia Connelly College Uyo.
                  </p>
                  <div className="initiative-content__details">
                    <h4>Scholarship Terms</h4>
                    <ul>
                      <li><strong>Type:</strong> Full Annual Tuition & Supplies up to a maximum of N200,000.00 per student</li>
                      <li><strong>Special Provision:</strong> Five students from CCC Uyo are nominated annually by the school for NECO fee payment only (N40,000 each)</li>
                      <li><strong>Duration:</strong> Multi-year support up to graduation</li>
                    </ul>
                  </div>
                  <div className="initiative-content__details">
                    <h4>Conditions of the Award</h4>
                    <ul>
                      <li>Maintain good academic standing throughout the program</li>
                      <li>Submit academic reports at the end of each term/semester to the Foundation</li>
                      <li>Participate in mentorship, leadership, or Science, Technology, Engineering & Maths (STEM) Education programs for the Girl Child organized by the Foundation, as applicable</li>
                      <li>Represent the values of the Foundation, including inclusion and community service</li>
                      <li>Girls from indigent backgrounds are given priority</li>
                    </ul>
                  </div>
                </div>

                <div className="initiative-content__section">
                  <h3>Our Impact</h3>
                  <p>
                    To date, the Foundation has awarded <strong>15 STEM education scholarships</strong> to girls, supporting their academic journey and helping them access opportunities in science, technology, engineering, and mathematics.
                  </p>
                </div>

                <div className="initiative-content__section">
                  <h3>Girl-Child Leadership Empowerment</h3>
                  <p>
                    As part of our commitment to empowering young girls through Science, Technology, Engineering & Maths (STEM) Education for the Girl Child, we have implemented a "Catch Them Young" leadership empowerment program. This initiative focuses on early intervention and leadership development for girls in secondary education.
                  </p>
                  <p>
                    In our recent program, 45 students from Cornellia Connelly College Uyo received educational resources including two books each on leadership skills: "Discovering & Unleashing Your Potentials" and "Mini-Encyclopedia on Global Women Pioneers and Pacesetters." These resources are designed to inspire and guide young girls toward leadership roles in STEM fields and beyond.
                  </p>
                </div>

                <div className="initiative-content__section">
                  <h3>Our Programs</h3>
                  <ul>
                    <li>Inclusive STEM Learning Labs with modern equipment and resources for the Girl Child</li>
                    <li>One-on-one mentorship programs connecting girls with STEM professionals</li>
                    <li>Scholarship opportunities for higher education in Science, Technology, Engineering & Maths (STEM) fields for the Girl Child</li>
                    <li>Workshops and bootcamps on coding, robotics, and engineering</li>
                    <li>Career guidance and internship placement support</li>
                    <li>Leadership empowerment programs with educational resources</li>
                  </ul>
                </div>
              </div>
            </section>
          </>
        )}

        {isDisabilitiesPage && (
          <>
            <section className="initiative-hero" id="disabilities-advocacy">
              <div className="initiative-hero__content">
                <span className="initiative-hero__eyebrow">Our Focus Areas</span>
                <h1 className="initiative-hero__title">Disabilities Advocacy</h1>
                <p className="initiative-hero__description">
                  Championing disability rights through policy advocacy, assistive technology access, and community inclusion programs.
                </p>
              </div>
            </section>

            <section className="initiative-content">
              <div className="initiative-content__inner">
                <div className="initiative-content__section">
                  <h2>Advocating for Full Inclusion</h2>
                  <p>
                    Our Disabilities Advocacy initiative works tirelessly to ensure that people with disabilities have equal rights, opportunities, and access to resources. We believe in a world where disability is not a barrier to success, but rather a part of the rich diversity of human experience.
                  </p>
                  <p>
                    Through policy advocacy, community programs, and technology access, we create pathways for people with disabilities to thrive in all aspects of life—education, employment, and social participation. We also convened <strong>one Disability Rights Advocacy Network Roundtable</strong>, strengthening collaboration and policy engagement across disability stakeholders.
                  </p>
                </div>

                <div className="initiative-content__section">
                  <h3>Autism Awareness Campaign</h3>
                  <p>
                    In our commitment to raising awareness and providing support for people with autism and their families, we have partnered with Patrick's Speech and Language Centre to distribute educational resources about autism spectrum disorders.
                  </p>
                  <p>
                    As part of this collaborative effort, <strong>350 copies</strong> of a "Fact Sheet on Autism" were printed and distributed in Essien Udim Local Government Area, Akwa Ibom State. The fact sheets were made available in three languages—English, Pidgin, and Annang, ensuring accessibility across different communities and language groups. The contact details of Dr. Mrs. Idongesit Ubi from the Department of Mental Health, University of Uyo Teaching Hospital, were included in the fact sheet for medical care references.
                  </p>
                  <p>
                    This multilingual approach ensures that families and caregivers from diverse linguistic backgrounds can access crucial information about autism, early signs, support resources, and where to seek professional help. The inclusion of medical contact information provides direct pathways for families to access specialized care and support services.
                  </p>
                </div>

                <div className="initiative-content__section">
                  <h3>Support for Hearing Impaired Education</h3>
                  <p>
                    To celebrate International Sign Language Day on 23 September 2025 the Foundation, visited Wesley Schools 1 & 2 for Hearing Impaired Children Surulere, Lagos State, and donated:
                  </p>
                  <ul>
                    <li><strong>50 pieces</strong> of school uniforms</li>
                    <li>Two and half cartons of deodorant to the students</li>
                    <li><strong>N40K</strong> one-off stipend each to <strong>four Volunteer Teachers</strong></li>
                    </ul>
                  <p>
                    The Foundation also reached out to Radio stations to help sensitize the public on the rights and needs of Hearing-Impaired individuals on their programs for the day and throughout the week.
                  </p>
                  </div>

                <div className="initiative-content__section">
                  <h3>Disability Rights Advocacy Network Roundtable</h3>
                  <p>
                    The Foundation convened its inaugural annual Disability Rights Advocacy Network Roundtable on <strong>3rd December 2025</strong> on the theme - "Using Legal Frameworks To Advance Disability Rights In Healthcare And Governance".
                  </p>
                  <p>
                    The session was hosted in coalition with Patrick's Speech and Language Centre and Punuka Attorneys & Solicitors to raise awareness on existing interventions, opportunities, and gaps affecting persons with disabilities—particularly in governance and healthcare systems—and to explore how legal frameworks can be leveraged to address these issues.
                  </p>
                  <p>
                    The Roundtable brought together government representatives, legal experts, healthcare professionals, civil society organizations, community leaders, and persons with lived experience of disability and Panel discussions were held on:
                  </p>
                  <ul>
                    <li><strong>Legal Review:</strong> The Law as a Lifeline: Reimagining Disability Rights in Nigeria</li>
                    <li><strong>Medical Panel session:</strong> Medical Care for Persons with Disabilities: Bridging Gaps in Access, Awareness, and Support</li>
                    <li><strong>Discourse by distinguished Legislative and Executive Government officials:</strong> Advancing Inclusive Governance: From Commitment to Action in Strengthening Laws and Policies for the Empowerment of Persons with Disabilities</li>
                    <li><strong>Feedback from Persons with Disabilities and their Families:</strong> Living with Dignity: What We Need to Thrive, Not Just Survive</li>
                  </ul>
                  <p>
                    The Discrimination Against Persons with Disabilities (Prohibition) Act, 2018 and the Lagos State Special People's Law, were focused on in the discussions with a view to translating policy into practice. Discussions centered on persistent barriers faced by persons with disabilities in healthcare, education, employment, infrastructure, and governance. Participants emphasized that while Nigeria has progressive laws, weak enforcement, poor awareness, and limited coordination continue to undermine the rights, dignity, and full inclusion of persons with disabilities. Participants reaffirmed the principle that disability rights are human rights, and that inclusion benefits not only persons with disabilities but society as a whole. The event concluded with a strong call for collective action, accountability, and sustained advocacy.
                  </p>
                  <p>
                    A communique is to be issued to Government and relevant stakeholders with follow up actions taken.
                  </p>
                </div>

                <div className="initiative-content__section">
                  <h3>Our Programs</h3>
                  <ul>
                    <li>Policy advocacy and legislative engagement for disability rights</li>
                    <li>Assistive technology access and training programs</li>
                    <li>Community inclusion initiatives and awareness campaigns</li>
                    <li>Autism awareness and educational resource distribution</li>
                    <li>Accessibility audits and improvements for public spaces</li>
                    <li>Support groups and peer networks for people with disabilities</li>
                  </ul>
                </div>
              </div>
            </section>
          </>
        )}

        {isWomenPage && (
          <>
            <section className="initiative-hero" id="women-empowerment">
              <div className="initiative-hero__content">
                <span className="initiative-hero__eyebrow">Our Focus Areas</span>
                <h1 className="initiative-hero__title">Women Empowerment</h1>
                <p className="initiative-hero__description">
                  Leadership development workshops, micro-enterprise grants, and personal growth programs so women can thrive economically.
                </p>
              </div>
            </section>

            <section className="initiative-content">
              <div className="initiative-content__inner">
                <div className="initiative-content__section">
                  <h2>Empowering Women to Lead and Thrive</h2>
                  <p>
                    Our Women Empowerment initiative is committed to creating opportunities for women to achieve economic independence, leadership positions, and personal fulfillment. We recognize that when women are empowered, entire communities benefit.
                  </p>
                </div>

                <div className="initiative-content__section">
                  <h3>Vocational Tools Entrepreneurial Support Program</h3>
                  <p>
                    Our Vocational Tools Entrepreneurial Support Program is designed to provide practical resources that enable indigent women to start and sustain their own businesses. This initiative recognizes that access to the right tools and equipment is often a critical barrier preventing women from turning their skills and training into viable economic opportunities.
                  </p>
                  <p>
                    To ensure the program's effectiveness and sustainability, beneficiaries are selected from women who have already completed requisite self-sponsored training or apprenticeship programs. This approach ensures that recipients have the necessary skills and knowledge to effectively utilize the vocational tools provided.
                  </p>
                  <div className="initiative-content__details">
                    <h4>Program Details</h4>
                    <ul>
                      <li><strong>Beneficiaries:</strong> 6 indigent women received vocational tools for business start-up</li>
                      <li><strong>Eligibility:</strong> Women who have completed prior requisite self-sponsored training or apprenticeship</li>
                      <li><strong>Support Type:</strong> Vocational tools and equipment for business start-up</li>
                      <li><strong>Partnership:</strong> The Society of Saint Vincent De Paul CCOTP Ikeja GRA, Lagos State donated one of the grinding machines gifted to beneficiaries</li>
                    </ul>
                  </div>
                  <p>
                    This program not only provides the essential tools needed for business operations but also creates a pathway for economic independence. By supporting women who have already invested in their own training, we ensure that our resources are maximized and that beneficiaries are well-positioned for success in their chosen vocations.
                  </p>
                </div>

                <div className="initiative-content__section">
                  <h3>Widows Welfare Pack</h3>
                  <p>
                    The Foundation extended a helping hand to <strong>60 Widows</strong> in June 2025, gifting each 10kg rice, wrapper and N5K. More of such support is needed for this vulnerable group especially empowerment with vocational tools and grants for their small-scale businesses.
                  </p>
                </div>

                <div className="initiative-content__section">
                  <h3>Our Programs</h3>
                  <ul>
                    <li>Vocational tools support for business start-up</li>
                    <li>Leadership development workshops and professional development</li>
                    <li>Micro-enterprise grants and business mentorship</li>
                    <li>Financial literacy and entrepreneurship training</li>
                    <li>Networking events and mentorship opportunities</li>
                    <li>Personal growth programs</li>
                  </ul>
                </div>
              </div>
            </section>
          </>
        )}

        {isTermsPage && (
          <section className="legal-page" id="terms">
            <header className="legal-page__hero">
              <span className="legal-page__breadcrumb">Home / Terms & Conditions</span>
              <h1 className="legal-page__title">Terms & Conditions</h1>
              <p className="legal-page__last-updated">Last Updated: December 2025</p>
            </header>

            <div className="legal-page__content">
              <div className="legal-page__section">
                <h2>1. Introduction</h2>
                <p>
                  These Terms & Conditions govern your use of the Dr. VUE Foundation website ("the Site"). By accessing or using the Site, you agree to comply fully with these Terms. If you do not agree, please discontinue use of the Site immediately.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>2. Purpose of the Website</h2>
                <p>
                  The Site exists to provide information about the Foundation's programs, mission, and advocacy work, facilitate communication with supporters, volunteers, and partners, share resources and updates related to disability inclusion and rights, and promote awareness of disability laws and policies in Nigeria.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>3. Acceptable Use</h2>
                <p>
                  You agree not to engage in unauthorized access, hacking, or disruption of the Site, upload harmful content such as malware or defamatory materials, misuse the Foundation's name, content, or branding, use the Site for commercial activities unless expressly permitted, or impersonate the Foundation or its representatives.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>4. Intellectual Property Rights</h2>
                <p>
                  All materials on the Site, including text, images, logos, graphics, publications, and design are the intellectual property of Dr. VUE Foundation unless otherwise stated.
                </p>
                <p>
                  <strong>Permitted Use:</strong> Personal, educational, or advocacy use, and sharing content with proper attribution.
                </p>
                <p>
                  <strong>Prohibited Use:</strong> Commercial reproduction, and alteration or misrepresentation of materials.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>5. Contributions, Donations, and Forms (If Applicable)</h2>
                <p>
                  If the Site includes forms for donations, volunteering, or partnership, users must provide accurate information, donation transactions (if enabled) must comply with platform rules, and the Foundation reserves the right to decline partnerships or submissions.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>6. External Websites and Links</h2>
                <p>
                  The Site may contain links to external resources for additional information. The Foundation is not responsible for the accuracy, security, or privacy practices of external websites.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>7. Disclaimer of Warranties</h2>
                <p>
                  All information on this Site is provided "as is." We do not guarantee accuracy or completeness of content, continuous operation of the Site, error-free or secure access, or availability of external linked content.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>8. Limitation of Liability</h2>
                <p>
                  To the fullest extent permitted by law, Dr. VUE Foundation is not liable for losses resulting from use or inability to use the Site, damages caused by technical issues or cyber incidents, or reliance on information posted on the Site.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>9. Privacy Policy Integration</h2>
                <p>
                  Your use of the Site is also governed by our Privacy Policy. By accessing the Site, you acknowledge and agree to its terms.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>10. Termination of Access</h2>
                <p>
                  We reserve the right to suspend or terminate access to the Site, modify or remove content without prior notice, and restrict use for violations of these Terms.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>11. Modifications to These Terms</h2>
                <p>
                  We may update these Terms at any time. Continued use of the Site signifies acceptance of any modifications.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>12. Contact Information</h2>
                <p>
                  For questions about these Terms:
                </p>
                <p>
                  Email: <a href="mailto:drvuefoundation@gmail.com">drvuefoundation@gmail.com</a><br />
                  Address: Victoria Island, Lagos, Nigeria
                </p>
              </div>
            </div>
          </section>
        )}

        {isCookiePolicyPage && (
          <section className="legal-page" id="cookie-policy">
            <header className="legal-page__hero">
              <span className="legal-page__breadcrumb">Home / Cookie Policy</span>
              <h1 className="legal-page__title">Cookie Policy</h1>
              <p className="legal-page__last-updated">Last Updated: December 2025</p>
            </header>

            <div className="legal-page__content">
              <div className="legal-page__section">
                <h2>1. Introduction</h2>
                <p>
                  Dr VUE Foundation ("we", "our", "us") is committed to protecting your privacy and ensuring transparency in how we collect and use data. This Cookie Policy explains what cookies are, how we use them, and the choices you have regarding their use when you visit our website.
                </p>
                <p>
                  By using our website, you agree to the use of cookies in accordance with this policy, unless you disable them through your browser or cookie preferences.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>2. What Are Cookies?</h2>
                <p>
                  Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. They help websites function properly, remember user preferences, and improve user experience.
                </p>
                <p>
                  Cookies may be session cookies, which are deleted when you close your browser, or persistent cookies, which are stored on your device for a defined period. They may also be first-party cookies set by our website, or third-party cookies set by external services we use.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>3. How We Use Cookies</h2>
                <p>
                  Dr VUE Foundation uses cookies for the following purposes:
                </p>
                <p>
                  <strong>Essential Cookies:</strong> These cookies are necessary for the website to function properly. They enable core features such as secure access to website areas, form submissions, and navigation and page functionality. Without these cookies, the website may not work correctly.
                </p>
                <p>
                  <strong>Performance and Analytics Cookies:</strong> These cookies help us understand how visitors interact with our website by collecting information such as pages visited, time spent on pages, traffic sources, and error messages. This data is aggregated and anonymous and helps us improve our website, content, and services.
                </p>
                <p>
                  <strong>Functionality Cookies:</strong> Functionality cookies allow our website to remember choices you make, such as language preferences, region or location settings, and previously submitted form details (where applicable). These cookies enhance your experience but are not strictly necessary.
                </p>
                <p>
                  <strong>Third-Party Cookies:</strong> We may use trusted third-party services (such as analytics or embedded media tools) that place cookies on your device. These cookies are governed by the respective third parties' privacy and cookie policies. Dr VUE Foundation does not control how third parties use these cookies.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>4. Cookies and Personal Data</h2>
                <p>
                  Some cookies may collect limited personal data, such as IP addresses or device identifiers. Any personal data collected through cookies is processed in accordance with our Privacy Policy and applicable data protection laws.
                </p>
                <p>
                  We do not use cookies to sell personal data, track individuals for advertising purposes, or collect sensitive personal information.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>5. Managing and Disabling Cookies</h2>
                <p>
                  You can control or disable cookies at any time by adjusting your browser settings. Most browsers allow you to view cookies stored on your device, delete existing cookies, block all or specific cookies, and receive alerts before cookies are stored.
                </p>
                <p>
                  Please note that disabling certain cookies may affect the functionality and performance of our website. For guidance, visit your browser's help section.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>6. Changes to This Cookie Policy</h2>
                <p>
                  We may update this Cookie Policy from time to time to reflect changes in technology, legal requirements, or how we operate. Any updates will be posted on this page with a revised "Last Updated" date.
                </p>
                <p>
                  We encourage you to review this policy periodically.
                </p>
              </div>

              <div className="legal-page__section">
                <h2>7. Contact Us</h2>
                <p>
                  If you have any questions about this Cookie Policy or how we use cookies, please contact us:
                </p>
                <p>
                  Dr VUE Foundation<br />
                  Email: drvuefoundation@gmail.com
                </p>
              </div>
            </div>
          </section>
        )}

        {isPartnerPage && (
          <>
            <section className="partner-hero" id="partner">
              <div className="partner-hero__content">
                <span className="partner-hero__eyebrow">Join Our Mission</span>
                <h1 className="partner-hero__title">Partner With Us</h1>
                <p className="partner-hero__description">
                  Together, we can create lasting impact and empower communities through inclusion, education, and opportunity.
                </p>
              </div>
            </section>

            <section className="partner-content">
              <div className="partner-content__inner">
                <div className="partner-content__about">
                  <h2>About Our Partnership Program</h2>
                  <p>
                    The Dr. VUE Foundation is committed to creating equitable opportunities for people with disabilities, girls, and women. We believe that meaningful change happens when we work together with dedicated partners who share our vision of inclusion and empowerment.
                  </p>
                  <p>
                    Whether you're an individual, organization, or corporation, there are numerous ways to partner with us. From supporting our Science, Technology, Engineering & Maths (STEM) Education for the Girl Child programs and disability advocacy initiatives to empowering women through vocational training and leadership development, your partnership can make a significant difference in the lives of those we serve.
                  </p>
                  <p>
                    We welcome partnerships that align with our core values of humanism, leadership, and excellence. Together, we can build a more inclusive and equitable society where everyone has the opportunity to thrive.
                  </p>
                </div>

                <div className="partner-content__form-section">
                  <h3>Get in Touch</h3>
                  <p className="partner-content__form-intro">
                    Fill out the form below to express your interest in partnering with us. Our team will reach out to discuss how we can collaborate.
                  </p>
                  <form className="partner-form" onSubmit={(e) => e.preventDefault()}>
                    <div className="partner-form__group">
                      <label className="partner-form__label" htmlFor="partner-name">Full Name</label>
                      <input
                        id="partner-name"
                        name="name"
                        type="text"
                        className="partner-form__input"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>

                    <div className="partner-form__group">
                      <label className="partner-form__label" htmlFor="partner-email">Email Address</label>
                      <input
                        id="partner-email"
                        name="email"
                        type="email"
                        className="partner-form__input"
                        placeholder="you@example.com"
                        required
                      />
                    </div>

                    <div className="partner-form__group">
                      <label className="partner-form__label" htmlFor="partner-phone">Phone Number</label>
                      <input
                        id="partner-phone"
                        name="phone"
                        type="tel"
                        className="partner-form__input"
                        placeholder="+234 000 000 0000"
                        required
                      />
                    </div>

                    <div className="partner-form__group">
                      <label className="partner-form__label" htmlFor="partner-interest">
                        How do you want to partner or what area of interest?
                      </label>
                      <textarea
                        id="partner-interest"
                        name="interest"
                        className="partner-form__textarea"
                        rows="5"
                        placeholder="Tell us about your partnership interest, area of focus, or how you'd like to collaborate..."
                        required
                      />
                    </div>

                    <button type="submit" className="button button--primary partner-form__submit">
                      Submit Partnership Request
                    </button>
                  </form>
                </div>
              </div>
            </section>
          </>
        )}

        {!isAboutPage && !isPublicationsPage && !isTeamPage && !isGalleryPage && !isContactPage && !isStemPage && !isDisabilitiesPage && !isWomenPage && !isPartnerPage && !isTermsPage && !isCookiePolicyPage && (
          <>
            <section className="partner" id="partner">
              <div className="partner__inner">
                <span className="partner__tag">Partner With Us</span>
                <h2 className="partner__title">
                  Advocating Inclusion. Empowering People with Disabilities & Females. Championing Excellence.
                </h2>
                <p className="partner__description">
                  Join our community of allies investing in equitable access to education, empowerment resources, and mentorship.
                  Together, we can unlock opportunities and create sustainable change for women and individuals with disabilities.
                </p>
                <div className="partner__actions">
                  <a className="button button--secondary" href="/partner">Partner With Us</a>
                </div>
              </div>
            </section>

            <section className="overview" id="overview">
              <div className="overview__inner">
                <header className="overview__header">
                  <span className="overview__eyebrow">Dr. VUE Foundation</span>
                  <h2 className="overview__title">Established June 2025</h2>
                  <p className="overview__tagline">
                    Advocating Inclusion. Empowering People with Disabilities & Females. Championing Excellence.
                  </p>
                </header>

                <div className="overview__grid">
                  <article className="overview__card">
                    <h3 className="overview__card-title">Mission</h3>
                    <p className="overview__card-copy">
                      To advocate for laws and policies that promote awareness, uphold the rights and inclusion of people with disabilities, and champion Science, Technology, Engineering & Maths (STEM) Education for the Girl Child, while advancing the economic empowerment of women to drive gender equality and sustainable development.
                    </p>
                  </article>
                  <article className="overview__card">
                    <h3 className="overview__card-title">Vision</h3>
                    <p className="overview__card-copy">
                      To be a leading center of excellence advancing the rights and full inclusion of people with
                      disabilities, while empowering girls and women through education, leadership, and economic
                      self-sufficiency to build a more equitable and prosperous society.
                    </p>
                  </article>
                </div>
              </div>
            </section>

            <section className="programs" id="programs">
              <div className="programs__inner">
                <div className="programs__header">
                  <span className="programs__tag">Our Focus Areas</span>
                  <h2 className="programs__title">Science, Technology, Engineering & Maths (STEM) Education for the Girl Child, Disabilities Advocacy, Women Empowerment</h2>
                </div>

                <div className="programs__grid">
                  {programHighlights.map(program => {
                    const encodedImage = encodeURI(program.image);
                    return (
                    <article className="program-card" key={program.title}>
                      <div
                        className="program-card__media"
                        role="img"
                        aria-label={program.title}
                          style={{ backgroundImage: `url('${encodedImage}')` }}
                      />
                      <div className="program-card__body">
                        <h3 className="program-card__title">{program.title}</h3>
                        <p className="program-card__description">{program.description}</p>
                        <a href={program.slug} className="button button--text">Explore Initiative</a>
                      </div>
                    </article>
                    );
                  })}
                </div>
              </div>
            </section>

            <section className="newsletter" id="newsletter">
              <div className="newsletter__inner">
                <div className="newsletter__copy">
                  <span className="newsletter__tag">Stay Connected</span>
                  <h2 className="newsletter__title">Join Our Impact Circle</h2>
                  <p className="newsletter__description">
                    Subscribe for monthly stories of inclusion, program highlights, and opportunities to champion women and people
                    with disabilities.
                  </p>
                </div>
                <form className="newsletter__form">
                  <label className="newsletter__label" htmlFor="newsletter-email">
                    Email address
                  </label>
                  <div className="newsletter__controls">
                    <input
                      id="newsletter-email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      aria-label="Email address"
                      required
                    />
                    <button type="submit" className="button button--primary">
                      Subscribe
                    </button>
                  </div>
                  <p className="newsletter__privacy">
                    We respect your privacy. Expect one email per month — unsubscribe anytime.
                  </p>
                </form>
              </div>
            </section>
          </>
        )}
      </main>

      {!isAboutPage && !isPublicationsPage && !isTeamPage && !isGalleryPage && !isContactPage && !isStemPage && !isDisabilitiesPage && !isWomenPage && !isTermsPage && !isCookiePolicyPage && (
        <section className="year-summary">
          <div className="year-summary__inner">
            <div className="year-summary__card">
              <h2 className="year-summary__title">Making a Difference Together</h2>
              <div className="year-summary__content">
                <div 
                  className="year-summary__items"
                  style={{ transform: `translateX(-${currentSummaryIndex * 100}%)` }}
                >
                  {yearSummaryItems.map((item, index) => (
                    <div key={index} className="year-summary__item">
                      <div className="year-summary__number">{item.number}</div>
                      <p className="year-summary__text">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="year-summary__indicators">
                {yearSummaryItems.map((_, index) => (
                  <button
                    key={index}
                    className={`year-summary__indicator ${index === currentSummaryIndex ? 'active' : ''}`}
                    onClick={() => setCurrentSummaryIndex(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <footer className="site-footer" id="contact">
        <div className="site-footer__inner">
          <div className="site-footer__column site-footer__column--brand">
            <div className="site-footer__logo">
              <img src="/logo1.png" alt="Dr. VUE Foundation" className="site-footer__logo-image" />
            </div>
            <p className="site-footer__mission">
              Advocating inclusion, empowering people with disabilities and females, and championing excellence every day.
            </p>
            <ul className="site-footer__contact">
              <li><a href="tel:+2349156417249">+234 915 641 7249</a></li>
              <li><a href="https://maps.app.goo.gl/placeholder">Victoria Island, Lagos, Nigeria</a></li>
              <li><a href="mailto:info@drvufoundation.com">info@drvufoundation.com</a></li>
            </ul>
          </div>

          <div className="site-footer__column">
            <h3 className="site-footer__heading">Quick Links</h3>
            <ul className="site-footer__list">
              <li><a href="/">Home</a></li>
              <li><a href="/about">About</a></li>
              <li><a href="/publications">Publications</a></li>
              <li><a href="/team">Team</a></li>
              <li><a href="/gallery">Gallery</a></li>
            </ul>
          </div>

          <div className="site-footer__column">
            <h3 className="site-footer__heading">Resources</h3>
            <ul className="site-footer__list">
              <li><a href="/contact">Contact</a></li>
              <li><a href="/terms">Terms & Conditions</a></li>
              <li><a href="/cookie-policy">Cookie Policy</a></li>
              <li><a href="#volunteer">Volunteer</a></li>
            </ul>
          </div>

          <div className="site-footer__column site-footer__column--cta">
            <h3 className="site-footer__heading">Partner With Us</h3>
            <p>Join our community of allies creating equitable opportunities for women and people with disabilities.</p>
            <button className="button button--primary site-footer__donate" onClick={openDonateModal}>Donate</button>
            <div className="site-footer__social">
              <a href="#facebook" aria-label="Facebook">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M22 12a10 10 0 1 0-11.562 9.875v-6.988H7.898V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.462h-1.26c-1.242 0-1.63.771-1.63 1.562V12h2.773l-.443 2.887h-2.33v6.988A10.002 10.002 0 0 0 22 12Z" />
                </svg>
              </a>
              <a href="https://www.instagram.com/drvuefoundation?igsh=MW80Y3dxeGI2aHF4dQ==" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5A4.25 4.25 0 0 0 7.75 20.5h8.5a4.25 4.25 0 0 0 4.25-4.25v-8.5A4.25 4.25 0 0 0 16.25 3.5h-8.5Zm9.75 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-5.5 1.75a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Zm0 1.5a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Z" />
                </svg>
              </a>
              <a href="https://www.youtube.com/@DrVUEFoundation" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M21.582 7.2a2.248 2.248 0 0 0-1.583-1.59C18.25 5.2 12 5.2 12 5.2s-6.25 0-7.999.41A2.248 2.248 0 0 0 2.418 7.2C2 8.958 2 12 2 12s0 3.042.418 4.8a2.248 2.248 0 0 0 1.583 1.59c1.749.41 7.999.41 7.999.41s6.25 0 7.999-.41a2.248 2.248 0 0 0 1.583-1.59C22 15.042 22 12 22 12s0-3.042-.418-4.8ZM10 15.27V8.73L15.5 12 10 15.27Z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
        <div className="site-footer__base">
                  <span>© {new Date().getFullYear()} Dr. VUE Foundation. All rights reserved.</span>
          <span>Humanism • Leadership • Excellence</span>
        </div>
      </footer>

      {donateModalOpen && (
        <div className="donate-modal" onClick={closeDonateModal}>
          <div className="donate-modal__content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="donate-modal__close" 
              onClick={closeDonateModal}
              aria-label="Close donation modal"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            
            <h2 className="donate-modal__title">Make a Donation</h2>
            <p className="donate-modal__subtitle">Support Dr. VUE Foundation</p>

            <div className="donate-modal__tabs">
              <button 
                className={`donate-modal__tab ${paymentMethod === 'bank' ? 'active' : ''}`}
                onClick={() => setPaymentMethod('bank')}
              >
                Bank Transfer
              </button>
              <button 
                className={`donate-modal__tab ${paymentMethod === 'card' ? 'active' : ''}`}
                onClick={() => setPaymentMethod('card')}
              >
                Card Payment
              </button>
            </div>

            {paymentMethod === 'bank' && (
              <div className="donate-modal__bank-details">
                <div className="donate-modal__info-card">
                  <h3>Bank Account Details</h3>
                  <div className="donate-modal__detail-row">
                    <span className="donate-modal__label">Account Name:</span>
                    <span className="donate-modal__value">Dr. VUE Foundation</span>
                  </div>
                  <div className="donate-modal__detail-row">
                    <span className="donate-modal__label">Account Number:</span>
                    <span className="donate-modal__value donate-modal__value--highlight">2046826138</span>
                  </div>
                  <div className="donate-modal__detail-row">
                    <span className="donate-modal__label">Bank:</span>
                    <span className="donate-modal__value">First Bank Ltd</span>
                  </div>
                </div>
                <div className="donate-modal__instructions">
                  <p>Please transfer your donation to the account details above. After making your transfer, you can contact us at <a href="mailto:info@drvufoundation.com">info@drvufoundation.com</a> with your transaction reference.</p>
                </div>
              </div>
            )}

            {paymentMethod === 'card' && (
              <div className="donate-modal__card-form">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  alert('Card payment integration would be implemented here. For now, please use bank transfer.');
                }}>
                  <div className="donate-modal__form-group">
                    <label htmlFor="card-number">Card Number</label>
                    <input 
                      type="text" 
                      id="card-number" 
                      placeholder="1234 5678 9012 3456"
                      maxLength="19"
                      required
                    />
                  </div>
                  
                  <div className="donate-modal__form-row">
                    <div className="donate-modal__form-group">
                      <label htmlFor="expiry">Expiry Date</label>
                      <input 
                        type="text" 
                        id="expiry" 
                        placeholder="MM/YY"
                        maxLength="5"
                        required
                      />
                    </div>
                    
                    <div className="donate-modal__form-group">
                      <label htmlFor="cvv">CVV</label>
                      <input 
                        type="text" 
                        id="cvv" 
                        placeholder="123"
                        maxLength="4"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="donate-modal__form-group">
                    <label htmlFor="card-name">Cardholder Name</label>
                    <input 
                      type="text" 
                      id="card-name" 
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  
                  <div className="donate-modal__form-group">
                    <label htmlFor="amount">Amount (NGN)</label>
                    <input 
                      type="number" 
                      id="amount" 
                      placeholder="1000"
                      min="1"
                      required
                    />
                  </div>
                  
                  <button type="submit" className="button button--primary donate-modal__submit">
                    Donate Now
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
